import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils.js';
import type { JSX } from 'react';

/**
 * Build modal description items from subscription card data.
 * Handles three layouts:
 * 1. Device products with package contents, network specs, maintenance, and services (4 items in 2x2 grid)
 * 2. Non-device products with network specs (dotted separator format)
 * 3. Products with only services (single section)
 */
export const getModalDescriptionItems = (card?: SubCardWithAnalyticsV4Props) => {
  if (!card) {
    return [];
  }

  const items: { title?: string; content?: JSX.Element }[] = [];

  const hasDeviceContent = card.modalContent?.devicePackageContents || card.modalContent?.deviceMaintenance;
  const hasNetworkSpecs = card.modalContent?.networkSpecs?.length;

  if (hasDeviceContent && hasNetworkSpecs) {
    // Item 1 (top-left): Device package contents
    if (card.modalContent?.devicePackageContents) {
      items.push({
        title: card.modalContent.devicePackageContents.title,
        content: (
          <ul className={`${dsClass.MARGIN_0} ds-font-size--small`}>
            {card.modalContent.devicePackageContents.items.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
      });
    }

    // Item 2 (top-right): Network specs with dotted separator lines
    items.push({
      content: (
        <div className="ds-font-size--small">
          {card.modalContent?.networkSpecs?.map(spec => (
            <div key={spec.label} className="of-subscription-modal-network-spec">
              <div className="of-subscription-modal-network-spec__label">{spec.label}</div>
              <div className="of-subscription-modal-network-spec__separator"></div>
              <div className="of-subscription-modal-network-spec__value">
                <span>{spec.value}</span>
                {(spec.popoverText || spec.popoverContent) && (
                  <span className={dsClass.MARGIN_LEFT_2}>
                    <CL.Popover
                      triggerElement={<CL.Icon icon="information" size="s" type="light" color="neutral-700" />}
                      placement="right"
                      i18n_popover_contentText={spec.popoverText}
                    >
                      {spec.popoverContent}
                    </CL.Popover>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ),
    });

    // Item 3 (bottom-left): Device maintenance
    if (card.modalContent?.deviceMaintenance) {
      items.push({
        title: card.modalContent.deviceMaintenance.title,
        content: (
          <ul className={`${dsClass.MARGIN_0} ds-font-size--small`}>
            {card.modalContent.deviceMaintenance.items.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
      });
    }
  } else if (hasNetworkSpecs) {
    // No device content, but still show network specs with dotted separator
    items.push({
      content: (
        <div className="ds-font-size--small">
          {card.modalContent!.networkSpecs!.map(spec => (
            <div key={spec.label} className="of-subscription-modal-network-spec">
              <div className="of-subscription-modal-network-spec__label">{spec.label}</div>
              <div className="of-subscription-modal-network-spec__separator"></div>
              <div className="of-subscription-modal-network-spec__value">
                <span>{spec.value}</span>
                {(spec.popoverText || spec.popoverContent) && (
                  <span className={dsClass.MARGIN_LEFT_2}>
                    <CL.Popover
                      triggerElement={<CL.Icon icon="information" size="s" type="light" color="neutral-700" />}
                      placement="right"
                      i18n_popover_contentText={spec.popoverText}
                    >
                      {spec.popoverContent}
                    </CL.Popover>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ),
    });
  }

  // Item 4 (bottom-right): Additional services
  if (card.modalContent?.additionalServices) {
    items.push({
      title: card.modalContent.additionalServices.title,
      content: (
        <ul className={`${dsClass.MARGIN_0} ds-font-size--small`}>
          {card.modalContent.additionalServices.items.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ),
    });
  }

  return items;
};
